import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { Download, FileText, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { isoDateToLocalDate, normalizeToIsoDate } from '@/lib/date'
import type { JobReport } from '@/types'

function ReportField({
  label,
  value,
  multiline = false,
}: {
  label: string
  value: ReactNode
  multiline?: boolean
}) {
  return (
    <div className='grid min-h-8 grid-cols-[180px_minmax(0,1fr)] items-start gap-16 border-b border-[#e7e9ef] pb-2 text-sm font-medium leading-[1.4]'>
      <span className='text-[#3d434e]'>{label}</span>
      <span
        className={
          multiline
            ? 'min-w-0 flex-1 whitespace-pre-wrap break-words text-right text-[#171a20]'
            : 'min-w-0 flex-1 whitespace-pre-wrap break-words text-right text-[#171a20]'
        }
      >
        {value || '--'}
      </span>
    </div>
  )
}

function ListField({
  label,
  items,
  multiline = false,
}: {
  label: string
  items?: string[] | null
  multiline?: boolean
}) {
  return (
    <ReportField
      label={label}
      value={items?.length ? items.join(multiline ? '\n' : ', ') : undefined}
      multiline={multiline}
    />
  )
}

function isPdf(mime?: string | null, name?: string | null): boolean {
  return mime === 'application/pdf' || /\.pdf$/i.test(name ?? '')
}

function PhotosField({
  label,
  report,
  emptyLabel,
}: {
  label: string
  report: JobReport
  emptyLabel: string
}) {
  const photos = (report.report_photos ?? []).filter(photo => photo.url)

  if (photos.length === 0) {
    const count = report.report_photo_ids?.length ?? 0
    if (!count) return <ReportField label={label} value={undefined} />

    return (
      <div className='border-b border-[#e7e9ef] pb-2'>
        <p className='mb-2 text-sm font-medium leading-[1.4] text-[#3d434e]'>
          {label}
        </p>
        <div className='inline-flex h-10 items-center gap-2 rounded-xl border border-[#e7e9ef] bg-[#f4f5f8] px-3 text-sm font-medium text-[#171a20]'>
          <FileText className='size-4 text-[#ff5a1f]' />
          {emptyLabel}
        </div>
      </div>
    )
  }

  return (
    <div className='border-b border-[#e7e9ef] pb-2'>
      <p className='mb-2 text-sm font-medium leading-[1.4] text-[#3d434e]'>
        {label}
      </p>
      <div className='flex flex-wrap gap-2'>
        {photos.map(photo =>
          isPdf(photo.mime_type, photo.file_name) ? (
            <a
              key={photo.id}
              href={photo.url ?? undefined}
              target='_blank'
              rel='noopener noreferrer'
              className='flex h-20 w-[107px] flex-col items-center justify-center gap-1 overflow-hidden rounded border border-[#e7e9ef] bg-[#f4f5f8] p-2 text-center'
            >
              <FileText className='size-6 text-[#ff5a1f]' />
              <span className='line-clamp-2 break-all text-[10px] text-[#6c7285]'>
                {photo.file_name}
              </span>
            </a>
          ) : (
            <a
              key={photo.id}
              href={photo.url ?? undefined}
              target='_blank'
              rel='noopener noreferrer'
              className='block h-20 w-[107px] overflow-hidden rounded border border-[#e7e9ef]'
            >
              <img
                src={photo.url ?? undefined}
                alt={photo.file_name ?? ''}
                className='size-full object-cover'
              />
            </a>
          )
        )}
      </div>
    </div>
  )
}

function SignatureField({
  label,
  url,
}: {
  label: string
  url?: string | null
}) {
  return (
    <div className='border-b border-[#e7e9ef] pb-2'>
      <p className='mb-2 text-sm font-medium leading-[1.4] text-[#3d434e]'>
        {label}
      </p>
      {url ? (
        <img
          src={url}
          alt={label}
          className='max-h-24 max-w-full object-contain'
        />
      ) : (
        <p className='text-sm font-medium text-[#171a20]'>--</p>
      )}
    </div>
  )
}

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <div className='border-t border-[#e7e9ef] px-6 pt-4'>
      <h3 className='font-sans text-base font-semibold uppercase leading-[1.2] text-[#6c7285]'>
        {children}
      </h3>
    </div>
  )
}

function statusItems(report: JobReport, t: (key: string) => string) {
  if (report.status !== 'completed') return [t('reportView.reportDraft')]

  return [
    t('reportView.reportValidated'),
    report.sent_to_client ? t('reportView.sentToClient') : null,
    t('reportView.readyToGo'),
  ].filter((status): status is string => Boolean(status))
}

function productLabel(product: Record<string, unknown>) {
  return [product.name, product.concentration]
    .map(value => String(value ?? '').trim())
    .filter(Boolean)
    .join(' ')
}

function translateReportValue(
  t: TFunction<'jobs'>,
  prefixes: string[],
  value: string | null | undefined
): string {
  const normalized = value?.trim()
  if (!normalized) return ''

  for (const prefix of prefixes) {
    const translated = t(`${prefix}.${normalized}`, { defaultValue: '' })
    if (translated) return translated
  }

  return normalized
}

function translateReportList(
  t: TFunction<'jobs'>,
  prefixes: string[],
  items?: string[] | null
): string[] | undefined {
  const translated = items
    ?.map(item => translateReportValue(t, prefixes, item))
    .filter(Boolean)

  return translated?.length ? translated : undefined
}

function formatLongReportDate(
  iso: string,
  language: string | undefined
): string {
  const date = isoDateToLocalDate(normalizeToIsoDate(iso))
  if (!date) return ''

  const locale = language?.startsWith('fr') ? 'fr-FR' : language || 'fr-FR'
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date)
}

export function JobReportView({
  report,
  onDownload,
  onSend,
  downloading = false,
}: {
  report: JobReport
  onDownload?: () => void
  onSend?: () => void
  downloading?: boolean
}) {
  const { t, i18n } = useTranslation('jobs')
  const products = (report.products_used ?? [])
    .map(productLabel)
    .filter(Boolean)
  const materials = (report.materials_used ?? [])
    .map(material =>
      translateReportValue(t, ['report.treatedAreasOpts'], material.name)
    )
    .filter(Boolean)
  const statuses = statusItems(report, t)
  const additionalQuote = report.needs_quote ? t('report.yes') : t('report.no')
  const nextVisit = report.next_visit_date
    ? formatLongReportDate(
        report.next_visit_date,
        i18n.resolvedLanguage ?? i18n.language
      )
    : undefined

  return (
    <Card className='overflow-hidden rounded-2xl border-[#e7e9ef] shadow-[0_1px_8px_-1px_rgba(38,45,66,0.1),0_1px_4px_-1px_rgba(38,45,66,0.05)]'>
      <div className='flex items-center overflow-hidden py-4 pr-6'>
        <h2 className='border-l-4 border-[#ff5a1f] px-4 font-sans text-base font-semibold uppercase leading-[1.2] text-[#6c7285]'>
          {t('detail.reportsTitle')}
        </h2>
      </div>

      <div className='flex flex-col gap-4 bg-[#ecfdf5] px-6 pb-6 pt-4'>
        <div className='flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-dashed border-[#b0b5c3] pb-4 text-sm font-medium leading-[1.4] text-[#047857]'>
          {statuses.map((status, index) => (
            <span key={status} className='inline-flex items-center gap-2'>
              {index > 0 && (
                <span className='size-1 rounded-full bg-[#34d399]' />
              )}
              {status}
            </span>
          ))}
        </div>
        <div className='flex flex-wrap gap-4'>
          {onDownload && (
            <Button
              variant='outline'
              className='h-10 rounded-xl border-[#e7e9ef] bg-white px-3 text-base font-medium leading-none hover:bg-white'
              onClick={onDownload}
              disabled={downloading}
            >
              <Download className='mr-2 size-4' />
              {t('reportView.downloadPdf')}
            </Button>
          )}
          {onSend && (
            <Button
              variant='outline'
              className='h-10 rounded-xl border-[#e7e9ef] bg-white px-3 text-base font-medium leading-none hover:bg-white'
              onClick={onSend}
            >
              <Mail className='mr-2 size-4' />
              {t('reportView.sendToClient')}
            </Button>
          )}
        </div>
      </div>

      <SectionHeading>{t('reportView.step1Title')}</SectionHeading>
      <div className='flex flex-col gap-2 px-6 pb-6 pt-4'>
        <ReportField
          label={t('reportView.infestationLevel')}
          value={translateReportValue(
            t,
            ['report.infestation'],
            report.infestation_level
          )}
        />
        <ListField
          label={t('reportView.affectedAreas')}
          items={translateReportList(
            t,
            ['report.areas'],
            report.affected_areas
          )}
        />
        <ListField
          label={t('reportView.signsOfPresence')}
          items={translateReportList(
            t,
            ['report.signs'],
            report.signs_of_presence
          )}
        />
        <ReportField
          label={t('reportView.observations')}
          value={report.observation}
        />
        <ReportField
          label={t('reportView.customerUrgency')}
          value={translateReportValue(
            t,
            ['report.urgency'],
            report.urgency_level
          )}
        />
        <PhotosField
          label={t('reportView.photosEvidence')}
          report={report}
          emptyLabel={t('reportView.photoCount', {
            count: report.report_photo_ids?.length ?? 0,
          })}
        />
      </div>

      <SectionHeading>{t('reportView.step2Title')}</SectionHeading>
      <div className='flex flex-col gap-2 px-6 pb-6 pt-4'>
        <ReportField
          label={t('reportView.treatment')}
          value={translateReportValue(t, ['report.methods'], report.treatment)}
        />
        <ReportField
          label={t('reportView.productsUsed')}
          value={products.join(', ')}
        />
        <ReportField
          label={t('reportView.materialUsed')}
          value={materials.join(', ')}
        />
        <ListField
          label={t('reportView.treatedAreas')}
          items={translateReportList(
            t,
            ['report.treatedAreasOpts'],
            report.treated_areas
          )}
        />
        <ReportField
          label={t('reportView.actionsPerformed')}
          value={report.actions_performed}
          multiline
        />
      </div>

      <SectionHeading>{t('reportView.step3Title')}</SectionHeading>
      <div className='flex flex-col gap-2 px-6 pb-6 pt-4'>
        <ListField
          label={t('reportView.recommendations')}
          items={translateReportList(
            t,
            ['report.recommendations'],
            report.recommendations
          )}
          multiline
        />
        <ReportField
          label={t('reportView.additionalQuote')}
          value={additionalQuote}
        />
        {report.needs_quote && (
          <ReportField
            label={t('reportView.quoteSubject')}
            value={translateReportValue(
              t,
              ['report.quoteSubjects'],
              report.quote_subject
            )}
          />
        )}
        <ReportField
          label={t('reportView.quoteDescription')}
          value={report.needs_quote ? report.quote_description : undefined}
        />
        <ReportField label={t('reportView.nextVisit')} value={nextVisit} />
        <ReportField
          label={t('reportView.followUpNotes')}
          value={report.next_visit_notes}
        />
        <SignatureField
          label={t('reportView.customerSignature')}
          url={report.client_signature_url}
        />
        {report.technician_signature_url && (
          <SignatureField
            label={t('reportView.technicianSignature')}
            url={report.technician_signature_url}
          />
        )}
      </div>
    </Card>
  )
}
